import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Home, Clock, Star, Share2, Check, DollarSign, Globe, Award } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AdSlot } from "../components/AdSense";

export default function JobDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user?.phone) setPhone(user.phone);
  }, [user]);

  const money = (v) => (v == null ? "" : `$${Number(v).toFixed(0)}`);

  const openApply = () => {
    if (!user) {
      toast.error(t("detail.loginToApply"));
      return navigate("/login");
    }
    setModalOpen(true);
  };

  const submitApply = async () => {
    setApplying(true);
    try {
      await api.post(`/jobs/${id}/apply`, { message, phone });
      setApplied(true);
      setModalOpen(false);
      toast.success(t("detail.applySuccess"));
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === "string" && msg.toLowerCase().includes("ya")) { setApplied(true); setModalOpen(false); }
      toast.error(typeof msg === "string" ? msg : t("common.error"));
    } finally {
      setApplying(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    api.post(`/jobs/${id}/share`).catch(() => {});
    if (navigator.share) { try { await navigator.share({ title: job?.title, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success(t("detail.shareCopied")); }
  };

  if (loading) return <div className="py-24 text-center text-stone-muted">{t("jobs.loading")}</div>;
  if (!job) return <div className="py-24 text-center text-stone-muted">{t("jobs.noResults")}</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button data-testid="detail-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium text-stone-sub hover:text-brand mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("detail.back")}
      </button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-border shadow-sm overflow-hidden">
        {job.offers_housing && (
          <div className="bg-housing text-white text-sm font-bold tracking-wide uppercase px-6 py-2.5 flex items-center gap-2" data-testid="detail-housing-badge">
            <Home className="w-4 h-4" /> {t("card.housing")}
          </div>
        )}
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-stone-text">{job.title}</h1>
              <Link to="#" className="text-lg text-brand font-medium">{job.restaurant?.name}</Link>
              <div className="flex items-center gap-1 text-stone-muted mt-1">
                <MapPin className="w-4 h-4" /> {job.city}{job.state ? `, ${job.state}` : ""}{job.zip_code ? ` ${job.zip_code}` : ""}
              </div>
            </div>
            <button data-testid="detail-share-btn" onClick={share} className="p-2.5 rounded-full hover:bg-brand-soft text-stone-muted hover:text-brand transition-colors">
              <Share2 className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <div className="flex items-center gap-2 bg-brand-soft text-brand px-4 py-2 rounded-xl font-heading font-bold">
              <DollarSign className="w-4 h-4" />
              {money(job.salary_min)}{job.salary_max ? ` - ${money(job.salary_max)}` : ""} {t("card.perHour")}
            </div>
            {job.schedule && (
              <div className="flex items-center gap-2 bg-stone-bg text-stone-sub px-4 py-2 rounded-xl text-sm">
                <Clock className="w-4 h-4" /> {job.schedule}
              </div>
            )}
            <div className="flex items-center gap-2 bg-stone-bg text-stone-sub px-4 py-2 rounded-xl text-sm">
              {job.contract_type === "part_time" ? t("card.partTime") : t("card.fullTime")}
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-heading font-semibold text-lg text-stone-text mb-2">{t("detail.description")}</h3>
            <p className="text-stone-sub whitespace-pre-line">{job.description}</p>
          </div>

          {job.offers_housing && job.housing_details && (
            <div className="mt-6 bg-housing/10 border border-housing/30 rounded-xl p-4">
              <h3 className="font-heading font-semibold text-stone-text mb-1 flex items-center gap-2">
                <Home className="w-4 h-4 text-housing" /> {t("detail.housingDetails")}
              </h3>
              <p className="text-stone-sub text-sm">{job.housing_details}</p>
            </div>
          )}

          <div className="mt-6">
            <h3 className="font-heading font-semibold text-lg text-stone-text mb-2">{t("detail.requirements")}</h3>
            <div className="flex flex-wrap gap-2">
              {job.requires_english && (
                <span className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-stone-bg text-stone-sub border border-stone-border">
                  <Globe className="w-3.5 h-3.5" /> {t("detail.requiresEnglish")}
                </span>
              )}
              {job.requires_experience && (
                <span className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-stone-bg text-stone-sub border border-stone-border">
                  <Award className="w-3.5 h-3.5" /> {t("detail.requiresExp")}
                </span>
              )}
              {!job.requires_english && !job.requires_experience && (
                <span className="text-sm text-stone-muted">{t("detail.noRequirements")}</span>
              )}
            </div>
          </div>

          {job.benefits?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading font-semibold text-lg text-stone-text mb-2">{t("detail.benefits")}</h3>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-brand-soft text-brand">
                    <Check className="w-3.5 h-3.5" /> {b}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      <div className="sticky bottom-20 md:bottom-6 mt-6">
        <button
          data-testid="detail-apply-btn"
          onClick={openApply}
          disabled={applied || applying}
          className={`w-full py-4 rounded-xl font-heading font-bold text-lg transition-all shadow-lg ${
            applied ? "bg-housing text-white" : "bg-brand text-white hover:bg-brand-hover hover:-translate-y-0.5"
          } disabled:opacity-80`}
        >
          {applied ? `✓ ${t("detail.applied")}` : applying ? "..." : t("detail.apply")}
        </button>
      </div>

      <AdSlot className="mt-8" />

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="apply-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("candidate.applyModal.title")} {job.title}</DialogTitle>
            <DialogDescription>{job.restaurant?.name} · {job.city}{job.state ? `, ${job.state}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-sub mb-1">{t("candidate.applyModal.phone")}</label>
              <input
                data-testid="apply-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-sub mb-1">{t("candidate.applyModal.message")}</label>
              <textarea
                data-testid="apply-message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("candidate.applyModal.messagePlaceholder")}
                className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2.5 rounded-xl border border-stone-border font-medium text-stone-sub">
              {t("candidate.applyModal.cancel")}
            </button>
            <button data-testid="apply-submit" onClick={submitApply} disabled={applying} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
              {applying ? "..." : t("candidate.applyModal.send")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
